<template>
  <div class="multi-select"
       :class="{'multi-select_selected': showHighlight}">
    <div class="multi-select__header">
      <div class="multi-select__title-container" @click="toggleOpenState()">
        <span class="multi-select__header-title" v-if="column.header_label">{{column.header_label}}</span>
        <div class="multi-select__main-title">
          <span class="multi-select__title" v-html="column.label"></span>
          <span class="multi-select__arrow" v-if="!column.isDisabled"></span>
        </div>
      </div>
    </div>
    <div class="multi-select__overlay"
         v-show="isOpened" @click.stop="toggleOpenState()"></div>
    <div class="multi-select__dropdown" v-show="isOpened" @click.stop="() => {}"
         :class="{'multi-select__dropdown_wide': isDateRange, 'multi-select__dropdown_right-align': isNumber}">
      <div class="multi-select__sort-container">
        <base-sortable-header
          ref="sortRef"
          label=""
          :prop-name="column.property"
          :sort-config="sortConfig"
          :isNumber="isNumber"
          @sort="sort"
        />
      </div>
      <template v-if="showAdditionalFilters">
        <div class="multi-select__separator"/>
        <div v-if="column.isAdditionalFilterSearch" class="multi-select__additional-search">
          <input class="multi-select__additional-input" @input.stop.prevent="handleAdditionalFilterSearch" v-model="additionalSearch" type="text">
          <div class="multi-select__clear-button" @click="clearAdditionalSearch" v-if="additionalSearch.length"></div>
          <base-material-design-icon class="multi-select__search-button" icon="search" v-else />
        </div>
      </template>
      <div v-else class="multi-select__additional-select">
        <div
          v-for="item in column.additionalFiltersConfig"
          :key="item.prop"
          class="multi-select__additional-select__item"
          @click="selectAdditionalFilters(item.prop)"
          :class="{'multi-select__additional-select__item-selected': additionalFilter === item.prop}"
        >
          {{item.label}}
        </div>
      </div>
      <div class="multi-select__separator"/>
      <base-multi-select-filter-dropdown
        v-if="isMultiSelect"
        :ref="childRef"
        :options="column.options"
        :isSmartInput="column.isSmartInput"
        :property-name="column.property"
        v-model="isSelected"
        :predefinedFilter="predefinedOptions"
        @apply="applyFilters($event)"/>
      <base-input-filter-dropdown
        v-else-if="isInputName"
        :ref="childRef"
        :property-name="column.property"
        v-model="isSelected"
        :predefinedFilter="predefinedOptions"
        @apply="applyFilters($event)"/>
      <base-date-range-filter-dropdown
        v-else-if="isDateRange"
        :ref="childRef"
        v-model="isSelected"
        :predefinedFilter="predefinedDateRangeFilter"
        :property-name="column.property"
        @apply="applyFilters($event)"
        @close="close()"
      />
      <base-number-filter-dropdown
        v-else-if="isNumber"
        :ref="childRef"
        :clearOnCross="column.isClearOnCross"
        :property-name="column.property"
        :predefinedFilter="predefinedNumberFilter"
        v-model="isSelected"
        @apply="applyFilters($event)"
      />

      <div class="multi-select__footer">
        <button class="multi-select__button multi-select__button-footer multi-select__button-footer_cancel"
                @click.prevent="close()">Cancel
        </button>
        <button class="multi-select__button multi-select__button-footer multi-select__button-footer_cancel"
                @click.prevent="cancel(true)">Clear
        </button>
        <button class="multi-select__button multi-select__button-footer multi-select__button-footer_apply"
                @click="apply()">
          Apply
        </button>
      </div>
    </div>
    <div class="multi-select_highlighted" v-if="showHighlight"/>
  </div>
</template>

<script>
  import UrlPlanning from '../features/url-planning/UrlPlanning'

  /**
   * parent component for all dropdown filters on {@link UrlPlanning} page
   */
export default {
  name: 'BaseMultiSelectSortableHeader',
  created () {
    for (const prop in this.childComponentMap) {
      if (this.childComponentMap[prop].names.includes(this.column.property)) {
        this.childRef = this.childComponentMap[prop].ref
      }
    }
    if (this.column.showAdditionalFilters && typeof this.column.advancedFilterDefault !== 'undefined') {
      this.selectAdditionalFilters(this.column.advancedFilterDefault)
    }
  },
  props: {
    /**
     * column setting
     */
    column: {
      type: Object,
      required: true,
      default: () => {
      }
    },
    sortConfig: {
      type: Array
    },
    // values set outside of component
    predefinedOptions: {
      default: null
    },
    // date values set outside of component
    predefinedDateRangeFilter: {
      type: Object,
      default: null
    },
    // number values set outside of component
    predefinedNumberFilter: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      // flag controls the selected state of the header. Bound as v-model to the child component
      isSelected: false,
      /**
       * @alias _sortDirection
       * we need this variable to highlight header if sorting was applied to corresponding column
       * */
      sortDirection: '',
      additionalSearch: '',
      isOpened: false,
      // ref to shown child
      childRef: null,
      /**
       * groups column by children component of the header base on it's type
       */
      childComponentMap: {
        MULTI_SELECT: {
          names: [
            'url_status',
            'page_types',
            'brief_status',
            'snapshot_1_rank',
            'snapshot_2_rank',
            'section',
            'serp_features',
            'baseline_rank',
            'type',
            'fk_pivot_table',
            'fk_pivot_field',
            'is_active',
            'report_category',
            'category',
            'location',
            'agency_roles',
            'client_roles',
            'role',
            'agency',
            'client',
            'writing_status',
            'fk_user_id_strategist',
            'fk_user_id_writer'
          ],
          ref: 'multiselect'
        },
        DATE: {
          names: [
            'target_date',
            'timestamp_created',
            'timestamp_submitted',
            'timestamp_processed',
            'timestamp_failed',
            'timestamp_completed',
            'timestamp_deleted',
            'timestamp_last_received_data',
            'created',
            'updated',
            'last_ran',
            'trial_end_date',
            'last_agg',
            'query_time'
          ],
          ref: 'date'
        },
        // var 'keyword' in next line used only in 'Keywords.vue' to enable search and give ability to highlight selected sorting
        INPUT_NAME: {
          names: [
            'url',
            'name',
            'keyword',
            'pattern',
            'property_group_name',
            'query',
            'error',
            'report_completion',
            'snapshot_completion',
            'snapshot_feature_completion',
            'email'
          ],
          ref: 'url' },
        NUMBER: {
          names: [
            'msv',
            'keywords',
            'score',
            'diff_score',
            'clients_count',
            'id',
            'urls',
            'indexed_pages',
            'associated_urls',
            'baseline_score',
            'optimized_score',
            'percent_completed',
            'last_indexed_page',
            'ranking1',
            'ranking2',
            'keywordTotal',
            'totalMsv',
            'ranking1',
            'ranking2',
            'change',
            'changePercent'
          ],
          ref: 'number'
        }
      },
      additionalFilter: null
    }
  },
  watch: {
    isOpened (value) {
      this.getChild() && this.getChild().reset(value)
      this.$emit('toggleState', value)
    },
    sortConfig: {
      /**
       * change {@link #_sortDirection} by changing sortConfig
       */
      handler () {
        if (this.sortConfig) {
          const config = this.sortConfig.find(config => config.propertyName === this.column.property)
          if (config) {
            this.sortDirection = config.sortDirection
          } else {
            this.sortDirection = ''
          }
        } else {
          this.sortDirection = ''
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    toggleOpenState () {
      this.isOpened = !this.isOpened
    },
    close () {
      this.isOpened = false
    },
    cancel (clearAll = false) {
      if (this.isSelected && this.isInputName && !clearAll) {
        this.$emit('cancel')
      }
      this.getChild() && this.getChild().cancel()
      this.getChild() && this.getChild().apply()
      this.sort('clear')
      if (this.column.showAdditionalFilters && this.column.isAdditionalFilterSearch) {
        this.clearAdditionalSearch()
      }
    },
    selectAdditionalFilters (prop) {
      if (this.additionalFilter !== prop) {
        this.additionalFilter = prop
      } else {
        this.additionalFilter = 'clear'
      }
      this.$emit('additionalFilterApply', this.additionalFilter)
    },
    handleAdditionalFilterSearch () {
      this.$emit('additionalFilterApply', this.additionalSearch)
    },
    clearAdditionalSearch () {
      this.additionalSearch = ''
      this.handleAdditionalFilterSearch()
    },
    apply () {
      this.getChild() && this.getChild().apply()
    },
    getChild () {
      return this.$refs[this.childRef]
    },
    sort (direction) {
      this.$emit('sort', direction)
    },
    applyFilters (event) {
      this.$emit('apply', event)
      this.close()
    }
  },
  computed: {
    showHighlight () {
      return this.isSelected || this.isOpened || this.isSorted || this.additionalSearch.length
    },
    isNumber () {
      return this.childComponentMap.NUMBER.names.includes(this.column.property)
    },
    isDateRange () {
      return this.childComponentMap.DATE.names.includes(this.column.property)
    },
    isMultiSelect () {
      return this.childComponentMap.MULTI_SELECT.names.includes(this.column.property)
    },
    isInputName () {
      return this.childComponentMap.INPUT_NAME.names.includes(this.column.property)
    },
    showAdditionalFilters () {
      return this.column.showAdditionalFilters
    },
    isSorted () {
      return ['asc', 'desc'].includes(this.sortDirection)
    }
  }
}
</script>

<style lang="scss" scoped>
  .multi-select {
    position: relative;
    flex: 1 1 100%;
    height: 100%;

    &__separator {
      background-color: $rfx-light-steel;
      margin-left: 15px;
      height: 1px;
      margin-bottom: 5px;
    }

    &_highlighted {
      position: absolute;
      width: 100%;
      height: 2px;
      background-color: $rfx-bright-blue;
      bottom: 0;
    }

    &_selected {
      background: $rfx-light-grey;
    }

    &__sort-container {
      padding: 5px 10px;
      max-width: 250px;
    }

    &__sort-arrow-container {
      display: flex;
    }

    &__sort-arrow {
      font-size: 14px;
      color: $rfx-bright-blue;

      &_up {
      }

      &_down {
      }
    }

    &__header {
      display: flex;
      height: 100%;
      box-sizing: border-box;
      padding: 0;

      &:hover {
        background-color: $rfx-light-grey;
      }
    }

    &__title-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      flex: 1 1 100%;
      padding: 15px 0 15px 14px;
      position: relative;
    }

    &__footer {
      display: flex;
      margin: 10px;
      justify-content: flex-end;
    }

    &__title {
      display: flex;
      align-items: center;
      height: 100%;
      font-size: 14px;
      box-sizing: border-box;
      @include ellipsis;
      color: inherit;
      user-select: none;
    }

    &__header-title {
      font-size: 12px;
      color: $rfx-light-march;
    }

    &__main-title {
      width: 100%;
      display: flex;
      justify-content: flex-start;
    }

    &__arrow {
      flex: 0 0 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 3px;

      &::after {
        content: '';
        display: block;
        width: 7px;
        height: 7px;
        box-sizing: border-box;
        border-right: 2px solid $rfx-bright-blue;
        border-bottom: 2px solid $rfx-bright-blue;
        transform: rotate(45deg);
        transform-origin: center;
      }
    }

    &__additional-search {
      position: relative;
      padding: 5px 15px;
    }

    &__additional-input {
      border: none;
      border-bottom: 1px solid $rfx-bright-blue;
      height: 20px;
      flex: 1 1 100%;
      padding: 10px 0;
      width: 100%;
      outline: none;
    }

    &__clear-button,
    &__search-button {
      position: absolute;
      top: 13px;
      right: 7px;
      width: 16px;
      height: 16px;
      background-color: transparent;
      color: $rfx-dark-march;
    }

    &__clear-button {
      cursor: pointer;
      &:hover {
        opacity: 1;
      }

      &:before, &:after {
        position: absolute;
        left: 7px;
        top: 3px;
        content: '';
        height: 12px;
        width: 2px;
        background-color: currentColor;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9998;
      padding: 0;
      background: transparent;
    }

    &__dropdown {
      position: absolute;
      z-index: 9999;
      top: 100%;
      background-color: #fff;
      box-shadow: 0 0 1px 0 rgba(8, 8, 8, .24), 0 4px 15px 0 rgba(8, 8, 8, .24);
      cursor: auto;
      border-radius: 0 0 6px 6px;
      width: 100%;
      min-width: 200px;

      &_wide {
        width: 260px;
      }

      &_right-align {
        right: 0;
      }
    }

    &__additional-select {

      &__item {
        font-weight: 400;
        font-size: 12px;
        padding: 5px 15px;
        cursor: pointer;

        &-selected {
          color: $rfx-almost-white;
          background-color: $rfx-bright-blue;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__button {
      border: none;
      outline: none;
      cursor: pointer;
      font-size: 10px;
    }

    &__button-footer {
      padding: 7px 10px;
      border-radius: 4px;

      &_disabled {
        pointer-events: none;
        opacity: 0.5;
      }

      &_apply {
        background-color: $rfx-medium-march;
        color: #fff;

        &:disabled {
          pointer-events: none;
          background-color: #fff;
          color: $rfx-neutral-grey;
          cursor: default;
        }
      }

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_clear,
      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
        margin-right: 10px;
      }
    }
  }
</style>
